/** App shell: light sidebar with company switcher, top bar with search, wake bar. */
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useStore } from "../lib/store";
import { api, onSlow } from "../lib/api";
import { BRAND, coName } from "../lib/brand";
import { KEY_HINT, cap } from "../lib/format";
import { Avatar, Tile } from "./bits";
import { Icon } from "./Icon";
import { Palette } from "./Palette";
import { Coach } from "./Coach";
import { useGettingStarted } from "../pages/Welcome";
import { DevPanel } from "./DevPanel";
import dvNoTag from "../assets/brand/dv-notag.svg";

/** DocVault parts (ADR-0022, ADR-0023). Vault is the base; the rest are add-ons. App picker approved 2026-10-28 (option A). */
const PARTS = [
  { key: "vault", name: "Vault", sub: "Policies & Procedures", path: "/", settings: "", c: "#B4561C", bg: "#FBEEE5",
    d: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 13h6M9 17h4" /></> },
  { key: "clienthub", name: "TechHub", sub: "Client documentation", path: "/clienthub", settings: "/settings/clienthub", c: "#3B6E8F", bg: "#E6EEF3",
    d: <><rect x="3" y="4" width="18" height="7" rx="1.5" /><rect x="3" y="13" width="18" height="7" rx="1.5" /><path d="M7 7.5h.01M7 16.5h.01" /></> },
  { key: "reviewhub", name: "ReviewHub", sub: "Quarterly reviews", path: "/reviewhub", settings: "/settings/reviewhub", c: "#4A5462", bg: "#E8EBF0",
    d: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 15l3-3 3 2 4-5" /></> },
  { key: "vendorhub", name: "VendorHub", sub: "Vendors and evidence", path: "/vendorhub", settings: "/settings/vendorhub", c: "#8A5A00", bg: "#F8EED9",
    d: <><path d="M3 7l9-4 9 4-9 4z" /><path d="M3 7v10l9 4 9-4V7" /><path d="M12 11v10" /></> },
] as const;
type Part = (typeof PARTS)[number];
function PartIcon({ part, size = 18 }: { part: Part; size?: number }) {
  return <span className="pp-ic" style={{ color: part.c, background: part.bg }}><svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{part.d}</svg></span>;
}

function Sidebar() {
  const { me, p, ws, setWs, approvalsCount, draftsCount, tasksCount, toast } = useStore();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  useEffect(() => {
    if (!open && !appsOpen) return;
    const close = () => { setOpen(false); setAppsOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("click", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("click", close); document.removeEventListener("keydown", esc); };
  }, [open, appsOpen]);

  const t = ws === "ALL" ? null : BRAND[ws];
  const pick = (code: string) => {
    setWs(code);
    setOpen(false);
    toast(code === "ALL" ? "Showing all your companies." : `Switched to ${coName(code)}. Company-wide documents always show.`);
    if (loc.pathname.startsWith("/doc/")) nav("/library");
  };
  const onCls = ({ isActive }: { isActive: boolean }) => (isActive ? "on" : "");
  const gs = useGettingStarted();
  const libActive = loc.pathname === "/library" || loc.pathname.startsWith("/doc/");
  // ADR-0022: DocVault is a family. Vault (documents) is the base; TechHub is an add-on.
  const inHub = loc.pathname.startsWith("/clienthub");
  // ADR-0023: ReviewHub is a third part, for people with a ReviewHub role.
  const inRh = loc.pathname.startsWith("/reviewhub");
  // ADR-0024: VendorHub is a fourth part; it works without TechHub.
  const inVh = loc.pathname.startsWith("/vendorhub");
  const has = (k: Part["key"]) => k === "vault" || (k === "clienthub" ? p.chCodes.length > 0 : k === "reviewhub" ? p.rhCodes.length > 0 : p.vhCodes.length > 0);
  const mine = PARTS.filter((x) => has(x.key));
  // Admins also see add-ons that aren't on for them, with a way to the switch.
  const notOn = p.anyAdmin ? PARTS.filter((x) => !has(x.key)) : [];
  const cur = PARTS.find((x) => x.key === (inHub ? "clienthub" : inRh ? "reviewhub" : inVh ? "vendorhub" : "vault"))!;
  const showParts = mine.length > 1 || notOn.length > 0;

  return (
    <aside className="side">
      <Link className="sb-brand" to="/" aria-label="DocVault home"><img alt="DocVault" src={dvNoTag} /></Link>
      {showParts && (
        <div className="pp">
          <button className="pp-btn" aria-haspopup="menu" aria-expanded={appsOpen} aria-label={`App: ${cur.name}. Switch app`} onClick={(e) => { e.stopPropagation(); setOpen(false); setAppsOpen((o) => !o); }}>
            <PartIcon part={cur} />
            <div><div className="pp-name" style={{ color: cur.c }}>{cur.name}</div><div className="pp-sub">{cur.sub}</div></div>
            <span className="ch"><Icon name="chev" size={12} /></span>
          </button>
          {appsOpen && (
            <div className="pp-menu" role="menu" onClick={(e) => e.stopPropagation()}>
              <div className="pp-h">Switch to</div>
              {mine.map((x) => (
                <button key={x.key} role="menuitem" className={`pp-it ${x.key === cur.key ? "cur" : ""}`} onClick={() => { setAppsOpen(false); nav(x.path); }}>
                  <PartIcon part={x} size={16} /><div><b>{x.name}</b><small>{x.sub}</small></div>{x.key === cur.key && <span className="pp-chk">✓</span>}
                </button>
              ))}
              {notOn.length > 0 && <>
                <hr /><div className="pp-h">More add-ons</div>
                {notOn.map((x) => (
                  <button key={x.key} role="menuitem" className="pp-it off" title={`Turn ${x.name} on in Settings`} onClick={() => { setAppsOpen(false); nav(x.settings); }}>
                    <PartIcon part={x} size={16} /><div><b>{x.name}</b><small>{x.sub}</small></div><span className="pp-not">Not on</span>
                  </button>
                ))}
              </>}
            </div>
          )}
        </div>
      )}
      <div className="ws">
        <button className="ws-btn" aria-haspopup="true" aria-expanded={open} onClick={(e) => { e.stopPropagation(); setAppsOpen(false); setOpen((o) => !o); }}>
          {t?.logo ? (
            <div className="ws-logo"><img alt={t.name} src={t.logo} /><div className="ws-sub">Workspace</div></div>
          ) : (
            <>
              <Tile code={ws} />
              <div>
                <div className="ws-name">{ws === "ALL" ? "All my companies" : coName(ws)}</div>
                <div className="ws-sub">{ws === "ALL" ? "All workspaces" : "Workspace"}</div>
              </div>
            </>
          )}
          <span style={{ marginLeft: "auto", opacity: 0.6, display: "inline-flex" }}><Icon name="chev" size={12} /></span>
        </button>
        <div className={`ws-menu ${open ? "open" : ""}`} role="menu" onClick={(e) => e.stopPropagation()}>
          {p.myCodes.length > 1 && (
            <>
              <div className="ws-item" role="menuitem" onClick={() => pick("ALL")}>
                <Tile code="ALL" />
                <div><b>All my companies</b><small>{p.myCodes.length} workspaces</small></div>
                {ws === "ALL" && <span className="chk">✓</span>}
              </div>
              <hr />
            </>
          )}
          {me.companies.map((c) => (
            <div key={c.code} className="ws-item" role="menuitem" onClick={() => pick(c.code)}>
              <Tile code={c.code} />
              <div>
                <b>{coName(c.code, c.name)}</b>
                <small>{p.rolesIn(c.code).map(cap).join(" · ")}{BRAND[c.code]?.placeholder ? " · brand color placeholder" : ""}</small>
              </div>
              {ws === c.code && <span className="chk">✓</span>}
            </div>
          ))}
        </div>
      </div>
      <nav className="nav">
        {/* My tasks at the top of both parts (approved mockup 2026-10-26). */}
        {gs.show && <NavLink to="/welcome" className={onCls}><Icon name="flag" />Getting started <span className="count dim">{gs.done}/{gs.total}</span></NavLink>}
        <NavLink to="/tasks" className={onCls} data-tour="tasks"><Icon name="tasks" />My tasks {tasksCount > 0 && <span className="count">{tasksCount}</span>}</NavLink>
        {inVh ? (
          <>
            <NavLink to="/vendorhub" className={() => (loc.pathname === "/vendorhub" && !loc.search.includes("show=due") || loc.pathname.startsWith("/vendorhub/v/") ? "on" : "")}><Icon name="box" />Vendors</NavLink>
            <NavLink to="/vendorhub?show=due" className={() => (loc.pathname === "/vendorhub" && loc.search.includes("show=due") ? "on" : "")}><Icon name="clock" />Due for review</NavLink>
          </>
        ) : inRh ? (
          <NavLink to="/reviewhub" className={() => (loc.pathname === "/reviewhub" || loc.pathname.startsWith("/reviewhub/r/") ? "on" : "")}><Icon name="chart" />Reviews</NavLink>
        ) : inHub ? (
          <>
            <NavLink to="/clienthub" end data-tour="clients" className={() => (loc.pathname === "/clienthub" || loc.pathname.startsWith("/clienthub/c/") ? "on" : "")}><Icon name="users" />Clients</NavLink>
            <NavLink to="/clienthub/agreements" className={onCls}><Icon name="sign" />Agreements</NavLink>
          </>
        ) : <>
        <NavLink to="/" end className={onCls}><Icon name="dash" />Dashboard</NavLink>
        <NavLink to="/library" className={() => (libActive ? "on" : "")} data-tour="library"><Icon name="lib" />Library</NavLink>
        {p.anyApprover && (
          <NavLink to="/approvals" className={onCls}><Icon name="ok" />Approvals {approvalsCount > 0 && <span className="count">{approvalsCount}</span>}</NavLink>
        )}
        {p.anyAuthor && (
          <NavLink to="/drafts" className={onCls}><Icon name="pen" />My drafts {draftsCount > 0 && <span className="count dim">{draftsCount}</span>}</NavLink>
        )}
        <NavLink to="/workflows" className={onCls}><Icon name="flow" />Workflows</NavLink>
        {p.anyAuthor && <NavLink to="/import" className={onCls}><Icon name="plus" />Import documents</NavLink>}
        </>}
        <div className="grp">Settings</div>
        <NavLink to={`/settings?part=${cur.key}`} className={() => (loc.pathname.startsWith("/settings") ? "on" : "")}><Icon name="cog" />Settings</NavLink>
      </nav>
      <div className="side-foot">
        <div className="powered">DocVault · from ITAssurance</div>
      </div>
    </aside>
  );
}

function crumbsFor(path: string, docNo?: string): string[] {
  if (path.startsWith("/doc/")) return ["Library", docNo ?? "Document"];
  if (path === "/clienthub") return ["TechHub", "Clients"];
  if (path === "/clienthub/agreements") return ["TechHub", "Agreements"];
  if (path.startsWith("/clienthub/c/")) return ["TechHub", "Clients", "Client"];
  if (path === "/workflows") return ["Vault", "Workflows"];
  if (path.startsWith("/workflows/")) return ["Vault", "Workflows", "Workflow"];
  if (path === "/reviewhub") return ["ReviewHub", "Reviews"];
  if (path.startsWith("/reviewhub/r/")) return ["ReviewHub", "Reviews", "Review"];
  if (path === "/vendorhub") return ["VendorHub", "Vendors"];
  if (path.startsWith("/vendorhub/v/")) return ["VendorHub", "Vendors", "Vendor"];
  if (path.startsWith("/new/")) return ["New revision"];
  if (path === "/new") return ["New document"];
  if (path === "/approvals") return ["Approvals"];
  if (path === "/drafts") return ["My drafts"];
  if (path === "/import") return ["Documents", "Import documents"];
  if (path === "/profile") return ["My profile"];
  if (path === "/tasks") return ["My tasks"];
  if (path === "/welcome") return ["Getting started"];
  if (path === "/help") return ["Help Center"];
  if (path.startsWith("/help/")) return ["Help Center", "Article"];
  if (path.startsWith("/settings/")) {
    const SET: Record<string, string> = {
      companies: "Companies", people: "Team", audit: "Audit log",
      approvals: "Reviews & approvals", codes: "Classification codes", acronyms: "Search acronyms", publishing: "Publishing keys", clienthub: "TechHub access", services: "Services", "agreement-types": "Agreement types",
      integrations: "Integrations", checklists: "Checklists", ai: "AI", reviewhub: "ReviewHub access", "reviewhub/ratings": "Rating definitions", "reviewhub/reminders": "Reminders and alerts", "reviewhub/page": "Client page", vendorhub: "VendorHub access", "vendorhub/rules": "Evidence and review rules",
    };
    const k = path.slice(10);
    return SET[k] ? ["Settings", SET[k]!] : ["Settings"];
  }
  if (path === "/library") return ["Library"];
  return [];
}

/** Top-right: who is signed in, with My profile, notifications, sign-in & security and Sign out. */
function UserMenu() {
  const { me, p } = useStore();
  const { signOut } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);
  const go = (to: string) => { setOpen(false); nav(to); };
  return (
    <div className="umenu">
      <button className="umenu-btn" aria-haspopup="true" aria-expanded={open} title="My profile" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
        <Avatar id={me.user.id} name={me.user.display_name} size={32} />
        <span className="hide-sm"><b>{me.user.display_name}</b><small>{p.primaryRoles.map(cap).join(" · ") || "No access yet"}</small></span>
        <Icon name="chev" size={12} />
      </button>
      {open && (
        <div className="umenu-pop" role="menu" onClick={(e) => e.stopPropagation()}>
          <div className="umenu-who"><b>{me.user.display_name}</b><small>{me.user.email}</small></div>
          <button role="menuitem" onClick={() => go("/profile")}><Icon name="user" size={16} />My profile</button>
          <button role="menuitem" onClick={() => go("/profile?tab=notifications")}><Icon name="clock" size={16} />Notifications</button>
          <button role="menuitem" onClick={() => go("/profile?tab=security")}><Icon name="lock" size={16} />Sign-in &amp; security</button>
          <button role="menuitem" onClick={() => go("/settings")}><Icon name="cog" size={16} />Settings</button>
          <button role="menuitem" onClick={() => go("/welcome")}><Icon name="flag" size={16} />Getting started</button>
          <hr />
          <button role="menuitem" onClick={() => { setOpen(false); signOut(); nav("/login"); }}>Sign out</button>
        </div>
      )}
    </div>
  );
}

function Topbar({ onSearch }: { onSearch: () => void }) {
  const { p, allDocs } = useStore();
  const loc = useLocation();
  const nav = useNavigate();
  const docNo = loc.pathname.startsWith("/doc/") ? allDocs?.find((d) => d.id === loc.pathname.slice(5))?.number : undefined;
  const crumb = crumbsFor(loc.pathname, docNo);
  return (
    <div className="top">
      <div className="in">
        {crumb.length >= 2 && (
          <div className="crumbs hide-sm">
            {crumb.map((c, i) => (i === crumb.length - 1 ? <b key={i}>{c}</b> : <span key={i}>{c} / </span>))}
          </div>
        )}
        <div className="searchbox" data-tour="search" role="button" tabIndex={0} onClick={onSearch} onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}>
          <Icon name="search" size={16} />
          <span className="ph">Search documents, numbers, acronyms…</span>
          <kbd>{KEY_HINT}</kbd>
        </div>
        <div className="spacer" />
        {/* New document only where documents are listed (Bill, 2026-10-23), not on every page. */}
        {p.anyAuthor && ["/", "/library", "/drafts"].includes(loc.pathname) && (
          <button className="btn btn-accent" onClick={() => nav("/new")}><Icon name="plus" />New document</button>
        )}
        <Link className={`helpbtn ${loc.pathname.startsWith("/help") ? "on" : ""}`} to="/help" title="Help Center: how-to articles and videos"><Icon name="help" size={17} /><span className="hide-sm">Help</span></Link>
        <UserMenu />
      </div>
    </div>
  );
}

/** First sign-in: open Getting started once (approved mockup 2026-10-26). */
function FirstSignIn() {
  const { onboarding, reloadOnboarding } = useStore();
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => {
    if (!onboarding || onboarding.tour_done) return;
    void api.setOnboarding({ tour_done: true }).then(() => reloadOnboarding());
    if (loc.pathname === "/" ) nav("/welcome", { replace: true });
  }, [onboarding?.tour_done]);
  return null;
}

export function Layout() {
  const [slow, setSlow] = useState(false);
  const [pal, setPal] = useState(false);
  useEffect(() => onSlow(setSlow), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPal(true); }
      else if (e.key === "/" && !/INPUT|TEXTAREA|SELECT/.test((document.activeElement as HTMLElement | null)?.tagName ?? "")) { e.preventDefault(); setPal(true); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  return (
    <div className="shell">
      <Sidebar />
      <main>
        <div className={`wake ${slow ? "show" : ""}`} role="status">
          <b>Waking up the database…</b> The library was idle, so this first load takes a few seconds.
        </div>
        <Topbar onSearch={() => setPal(true)} />
        <div className="page"><Outlet /></div>
      </main>
      {pal && <Palette onClose={() => setPal(false)} />}
      <Coach />
      <FirstSignIn />
      <DevPanel />
    </div>
  );
}
